import { useEffect, useState } from "react";
import { IAssessmentViewProps } from "../types/interfaces/IAssessmentViewProps";
import { IIndividualLearningPlan } from "../types/interfaces/IIndividualLearningPlan";
import { IlpData } from "../types/classes/IlpData";
import { useDuesSponsoredOrgContext } from '../../global/hooks/useDuesSponsoredOrgContext';
import { useIlpContext } from "../hooks/useIlpContext";
import PlanMetaData from "./PlanMetaData";
import CareerGoals from "./CareerGoals";
import SelfAssessment from "./SelfAssessment";
import LearningObjectives from "./LearningObjectives";
import AssessmentAction from "./AssessmentAction";
import { Box } from "@mui/material";
import { ICompetency } from "../types/interfaces/ICompetency";
import usePostIlp from "../hooks/usePostIlp";
import useUpdateObjectives from "../hooks/useUpdateObjectives";
import AddComment from "./AddComment";
import CommentList from "./CommentList";
import useSaveComment from "../hooks/useSaveComment";

//This component is responsible for rendering an assessment view.
const AssessmentView: React.FC<IAssessmentViewProps> = ({ plan, onSave, onCancel, onCommentSave, onPrint }) => {
    const [goals, setGoals] = useState(plan.LearningPlan.PlanGoals);
    const [objectives, setObjectives] = useState(plan.LearningPlan.PlanObjectives);
    const [competencies, setCompetencies] = useState(plan.Competencies);
    const [areButtonsDisabled, setAreButtonsDisabled] = useState(false);
    const { selectedOrganization } = useDuesSponsoredOrgContext();
    const { role, isAdvisorFlag, isAdminFlag, customerKey, expandAll, setExpandAll } = useIlpContext();

    const API_SAVE_ILP = process.env.REACT_APP_API_SAVE_ILP;
    const API_APPROVE_ILP = process.env.REACT_APP_API_APPROVE_ILP;
    const API_SUBMIT_ILP = process.env.REACT_APP_API_SUBMIT_ILP;
    const API_UPDATE_OBJECTIVES = process.env.REACT_APP_API_UPDATE_OBJECTIVES;
    const API_SAVE_COMMENT = process.env.REACT_APP_API_SAVE_COMMENT;
    const ADVISOR_KEY = process.env.REACT_APP_ADVISOR_KEY;

    let apiEndPoint;

    if (isAdvisorFlag || isAdminFlag) {
        apiEndPoint = `${API_APPROVE_ILP}`;
    } else {
        apiEndPoint = `${API_SAVE_ILP}`;
    }

    const postIlpFetch = usePostIlp(apiEndPoint);
    const apiSubmitEndPoint = `${API_SUBMIT_ILP}`;
    const submitIlpFetch = usePostIlp(apiSubmitEndPoint);
    const apiUpdateObjectivesEndPoint = `${API_UPDATE_OBJECTIVES}${plan.IlpKey}`;
    const updateObjectivesFetch = useUpdateObjectives(apiUpdateObjectivesEndPoint);
    const apiSaveCommentEndPoint = `${API_SAVE_COMMENT}${plan.IlpKey}${ADVISOR_KEY}${customerKey}`;
    const saveCommentFetch = useSaveComment(apiSaveCommentEndPoint);

    //Function checks if all requirements are meet for submit.
    const isAssessmentComplete = () => {
        return (
            (competencies.every((competency: { SubCompetencies: ICompetency[] }) =>
                competency.SubCompetencies.every(
                    (subCompetency: ICompetency) =>
                        !subCompetency.IsOptional &&
                        subCompetency.Rating !== null &&
                        subCompetency.Rating !== ""
                )
            ) ||
                competencies.every(
                    (competency: ICompetency) =>
                        !competency.IsOptional &&
                        competency.Rating !== null &&
                        competency.Rating !== ""
                )) &&
            selectedOrganization !== null &&
            selectedOrganization?.Name !== null &&
            goals !== null &&
            goals?.length > 0 &&
            !plan.LearningPlan.IsOptionalGoals &&
            objectives !== null &&
            objectives?.length > 0 &&
            !plan.LearningPlan.IsOptionalObjectives
        );
    };

    useEffect(() => {
        isAssessmentComplete();
    }, [goals, objectives, competencies]);

    //Function to handle accordion closure after print.
    const handleCloseAll = () => {
        setExpandAll(false);
    }

    // Set listener for print close event.
    window.addEventListener("afterprint", handleCloseAll);

    function delay(milliseconds : number) {
        return new Promise(resolve => {
            setTimeout(resolve, milliseconds);
        });
    }

    //Function to handle print.
    const handlePrint = async () => {
        onPrint(plan);
        await delay(1000);
        window.print();
    }

    //Function to handle goals change.
    const handleGoalsChange = (changedGoals: string) => {
        setGoals(changedGoals);
    };

    //Function to handle objectives change.
    const handleObjectivesChange = (changedObjectives: string) => {
        setObjectives(changedObjectives);
    };

    //Function to handle ratings change.
    const handleRatingsChange = (key: string, rating: string | null) => {
        setCompetencies((prevCompetencies: ICompetency[]) => {
            const updateRating = (competency: ICompetency): ICompetency => {
                if (competency.CompetencyKey === key) {
                    return { ...competency, Rating: rating };
                }
                return {
                    ...competency,
                    SubCompetencies: competency.SubCompetencies?.map(updateRating),
                };
            };
            return prevCompetencies.map(updateRating);
        });
    };

    //Function to handle an ilp save.
    const handleSave = async () => {
        setAreButtonsDisabled(true);
        const savePlan: IIndividualLearningPlan = {
            ...plan,
            LearningPlan: {
                ...plan.LearningPlan,
                PlanGoals: goals,
                PlanObjectives: objectives,
            },
            Competencies: competencies,
        };

        if (!savePlan.IlpKey && !savePlan.LearningPlan.PlanStatus) {
            savePlan.LearningPlan.PlanStatus = "Started";
        }

        if (isAdvisorFlag || isAdminFlag) {
            const updatedPlan = {
                ...savePlan,
                LearningPlan: {
                    ...savePlan.LearningPlan,
                    Approver: {
                        CustomerKey: customerKey,
                        FirstName: null,
                        LastName: null,
                        Email: null,
                    },
                },
            };
            const postedIlp = await postIlpFetch(updatedPlan);
            onSave(postedIlp);
        } else {
            const postedIlp = await postIlpFetch(savePlan);
            onSave(postedIlp);
        }
    };

    //Function to handle an ilp submit.
    const handleSubmit = async () => {
        setAreButtonsDisabled(true);
        const submitPlan: IIndividualLearningPlan = {
            ...plan,
            LearningPlan: {
                ...plan.LearningPlan,
                PlanGoals: goals,
                PlanObjectives: objectives,
            },
            Competencies: competencies,
        };
        submitPlan.LearningPlan.PlanStatus = "Submitted";
        const submittedIlp = await submitIlpFetch(submitPlan);
        onSave(submittedIlp);
    };

    //Function to handle update objectives.
    const handleUpdateObjectives = async () => {
        const updatedPlan: IIndividualLearningPlan = {
            ...plan,
            LearningPlan: {
                ...plan.LearningPlan,
                PlanGoals: goals,
                PlanObjectives: objectives,
            },
            Competencies: competencies,
        };

        if (plan.IlpKey !== null) {
            let ilpData = new IlpData<string>(role, objectives);
            await updateObjectivesFetch(ilpData);
        }
        onSave(updatedPlan);
    };

    //Function to handle a plan comment save.
    const handleCommentSave = async (comment: string) => {
        let ilpData = new IlpData<string>(role, comment);
        const savedPlanComment = await saveCommentFetch(ilpData);
        const updatePlan: IIndividualLearningPlan = {
            ...plan, LearningPlan: {
                ...plan.LearningPlan,
                PlanComments: plan.LearningPlan.PlanComments ?
                    [...plan.LearningPlan.PlanComments, savedPlanComment] :
                    [savedPlanComment]
            }
        };
        if (onCommentSave) onCommentSave(updatePlan);
    };

    return (
        <>
            <PlanMetaData plan={plan} />
            <CareerGoals
                isReadOnly={plan.LearningPlan.IsReadOnlyGoals}
                goals={plan.LearningPlan.PlanGoals}
                onGoalsChange={handleGoalsChange}
            />
            <SelfAssessment
                competencies={competencies}
                onRatingChange={handleRatingsChange}
            />
            <LearningObjectives
                isReadOnly={plan.LearningPlan.IsReadOnlyObjectives}
                objectives={plan.LearningPlan.PlanObjectives}
                onObjectivesChange={handleObjectivesChange}
            />
            <CommentList planComments={plan.LearningPlan.PlanComments} />
            {(isAdvisorFlag || isAdminFlag) &&
                plan.LearningPlan.PlanStatus === "Submitted" &&
                <AddComment onCommentSave={handleCommentSave} />}
            <Box sx={{ mt: 1, mb: 1 }}>
                <AssessmentAction
                    onSave={handleSave}
                    onCancel={onCancel}
                    planStatus={plan.LearningPlan.PlanStatus}
                    submitFlag={isAssessmentComplete()}
                    onSubmit={handleSubmit}
                    onUpdateObjectives={handleUpdateObjectives}
                    onPrint={handlePrint}
                    areButtonsDisabled={areButtonsDisabled}
                />
            </Box>
        </>
    );
};

export default AssessmentView;